"use client"

import axios from "axios"
import React, { useEffect, useRef, useState } from "react"
import { NotificationManager } from "react-notifications"
import { useSelector } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import DownArrow from "../../assets/svgs/down-arrow.svg"
import Plus from "../../assets/svgs/Plus.svg"
import sortAsce from "../../assets/svgs/sort-ascending.svg"
import sortDesc from "../../assets/svgs/sort-descending.svg"
import swap from "../../assets/svgs/swap-vertical.svg"
import UpArrow from "../../assets/svgs/up-arrow.svg"
import ActionsMenu from "../../components/ActionsMenu"
import ConfirmationModal from "../../components/ConfirmationModals/ConfirmationModal"
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal"
import Loader from "../../components/Loader"
import ContactPagination from "../../components/Pagination/ContactPagination"
import useAuth from "../../hooks/useAuth"
import BaseLayout from "../../layouts/BaseLayout"
import { BASE_URL } from "../../utils/Element"
import { initialContractCloseFilterData } from "../../utils/initialData"
import ContractCloseFilter from "./components/ContractCloseFilter"
import ExpandedRowContent from "./components/ExpandedRowContent"
import PropertyPortfolioModal from "./components/PropertyPortfolioModal"

const ContractToClosed = () => {
  const [config] = useAuth()
  const navigate = useNavigate()

  // ─── Search + Debounce states ────────────────────────────────
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const debounceTimer = useRef(null)
  const [listData, setListData] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState("")
  const [selectedPortfolioId, setSelectedPortfolioId] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [itemPerPage, setItemPerPage] = useState(10)
  const [sortBy, setSortBy] = useState("id")
  const [sortDirection, setSortDirection] = useState("asc")
  const [activeTab, setActiveTab] = useState(0)
  const [disableDelete, setDisableDelete] = useState(false)
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed)
  const [expandedRows, setExpandedRows] = useState({})
  const [paginationData, setPaginationData] = useState({
    totalItems: 0,
    from: 0,
    to: 0,
    totalPages: 0,
  })

  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData")
    return localFilterData ? JSON.parse(localFilterData) : initialContractCloseFilterData
  })

  // ─── Debounce logic ───────────────────────────────────────────
  const handleSearchChange = (e) => {
    const value = e.target.value
    // Optional: remove leading spaces
    const trimmed = value.startsWith(" ") ? value.trimStart() : value
    setSearch(trimmed)
  }

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(() => {
      // Trigger only if empty or ≥ 3 characters
      if (search.length === 0 || search.length >= 3) {
        setDebouncedSearch(search.trim())
      }
    }, 450)

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [search])

  // ─── Query builder – use debouncedSearch instead of raw search ───
  const buildQueryParams = (filters) => {
    let params = `&page=${currentPage}&per_page=${itemPerPage}`

    // Use debounced value
    if (debouncedSearch) {
      params += `&search=${encodeURIComponent(debouncedSearch)}`
    }

    const { current_phase, next_action } = filters

    if (next_action) {
      params += `&next_action=${encodeURIComponent(next_action)}`
    }
    if (current_phase) {
      params += `&current_phase=${encodeURIComponent(current_phase)}`
    }
    if (sortDirection && sortBy !== "id") {
      params += `&sort_direction=${sortDirection}`
    }
    if (sortBy && sortBy !== "id") {
      params += `&sort_by=${sortBy}`
    }

    return params
  }

  const fetchData = (filters = filterData) => {
    const queryParams = buildQueryParams(filters)
    setLoading(true)

    axios
      .get(`${BASE_URL}/contract-to-close-listing?${queryParams}&active_tab=${activeTab}`, config)
      .then((res) => {
        const value = res?.data?.data?.contracts_to_close
        setListData(value?.data || [])
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        })
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message)
        }
      })
      .finally(() => setLoading(false))
  }

  // ─── Main triggers ────────────────────────────────────────────
  useEffect(() => {
    fetchData(filterData)
  }, [sortDirection, sortBy, debouncedSearch, currentPage, itemPerPage, activeTab])

  // Reset page when search / tab / filters change significantly
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, activeTab])

  const onSuccess = (filters) => {
    fetchData(filters || filterData)
  }

  const handleNext = () => {
    if (currentPage < paginationData.totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  const handleDelete = () => {
    setDisableDelete(true)
    axios
      .delete(`${BASE_URL}/contract-to-close/${selectedId}`, config)
      .then((res) => {
        setSelectedId("")
        onSuccess()
        setShowDeleteModal(false)
        if (res?.data?.message) NotificationManager.success(res.data.message)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
      .finally(() => setDisableDelete(false))
  }

  const handleConfirm = () => {
    axios
      .put(`${BASE_URL}/mark-closed/${selectedId}`, { closed: true }, config)
      .then((res) => {
        setSelectedId("")
        onSuccess()
        setShowConfirmModal(false)
        if (res?.data?.message) NotificationManager.success(res.data.message)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 1: return "text-red-500"
      case 2: return "text-gray-500"
      case 3: return "text-black-500"  // or text-green-600 etc.
      default: return "text-red-500"
    }
  }

  return (
    <BaseLayout>
      <div className="contact-header">
        <p className="head-1 dark-H">Contract to Close</p>
        <div className="md:flex block gap-6 items-center">
          <div className="flex gap-6 justify-between items-center">
            <ContractCloseFilter
              filterData={filterData}
              onSetFilterData={setFilterData}
              from="Contract-To-Close"
              onCallApiAgain={onSuccess}
            />

            <div className="search-box contacts">
              <input
                type="text"
                className="body-S"
                placeholder="Search name, address, city, state..."
                value={search}
                onChange={handleSearchChange}           // ← changed
                style={{ border: "1px solid #D8D8D8", marginTop: "0px" }}
              />
            </div>

            <button
              className="add-contact-button green-bg-H light-L body-S"
              onClick={() => navigate("/add-contact-close")}
            >
              <img className="mr-2" src={Plus} alt="plus" /> Add
            </button>
          </div>
        </div>
      </div>

      <div className="task-tabs mx-1 mt-2 flex justify-between body-L dark-M mb-4">
        <div className="flex gap-4">
          {["Stage View", "List View"].map((tab, index) => (
            <p
              key={index}
              role="button"
              className={`${activeTab === index ? "head-4 dark-H active" : ""} py-1`}
              onClick={() => {
                setActiveTab(index)
                setCurrentPage(1)
              }}
            >
              {tab}
            </p>
          ))}
        </div>
      </div>

      {activeTab === 0 && (
        <div className="light-bg-L pb-3 table-container">
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
            {loading ? (
              <Loader />
            ) : listData?.length === 0 ? (
              <p className="body-N text-center mt-6 p-10">No Data Available</p>
            ) : (
              <table className="contact-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    <th><p>Details</p></th>
                    <th className="green-H">
                      <div className="table-header">
                        Property
                        <img
                          role="button"
                          src={sortBy !== "property_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("property_name")
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                          }}
                          alt="sort icon"
                        />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        Key Contacts
                        <img
                          role="button"
                          src={sortBy !== "key_contacts" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("key_contacts")
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                          }}
                          alt="sort icon"
                        />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        Next Milestone
                        <img
                          role="button"
                          src={sortBy !== "next_Milestone" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("purchase_agreement_draft_distributed")
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                          }}
                          alt="sort icon"
                        />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        Closing Progress
                        <img
                          role="button"
                          src={sortBy !== "executed_purchase_agreeement" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("executed_purchase_agreeement")
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                          }}
                          alt="sort icon"
                        />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {listData.map((el) => (
                    <React.Fragment key={el.id}>
                      <tr
                        className="dark-M body-N cursor-pointer"
                        onClick={(e) => {
                          if (e.target.closest(".actions-menu") || e.target.closest(".arrow-icon")) return
                          navigate(`/stage_detail/${el.id}`)
                        }}
                      >
                        <td>
                          <img
                            role="button"
                            src={expandedRows[el.id] ? UpArrow : DownArrow}
                            alt="expand"
                            className="sidebar-icons arrow-icon"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleRowDetails(el.id)
                            }}
                          />
                        </td>

                        <td className="head-5 capitalize flex items-start gap-3">
                          {el?.portfolio === 1 && (
                            <p
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPortfolioId(el?.link?.id);
                                setShowPropertyModal(true);
                              }}
                            >
                              +
                            </p>
                          )}

                          <div className="flex-1">
                            <Link
                              to={
                                el?.portfolio === 0
                                  ? `/property/${el?.property_id}`
                                  : el?.link?.owner_info_type === "contact"
                                    ? `/contact/${el?.link?.owner?.id}`
                                    : `/company/${el?.link?.owner?.id}`
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                if (el?.portfolio === 0) {
                                  navigate(`/property/${el?.property_id}`);
                                } else if (el?.link?.owner_info_type === "contact") {
                                  navigate(`/contact/${el?.link?.owner?.id}`);
                                } else if (el?.link?.owner_info_type === "company") {
                                  navigate(`/company/${el?.link?.owner?.id}`);
                                }
                              }}
                            >
                              {el?.link?.property_name} <br />
                              {el?.link?.street_address} <br />
                              {el?.link?.state && el?.link?.city ? ` ${el?.link?.city} ,  ${el?.link?.state} ` : ""}
                            </Link>
                          </div>
                        </td>

                        <td>
                          <div>
                            <p>{el?.key_contact?.agent?.name}</p>
                            {el?.key_contact?.broker_details?.map((bd, i) => (
                              <p key={i}>
                                {bd?.first_name} {bd?.last_name}
                              </p>
                            ))}
                          </div>
                        </td>

                        <td>{el?.current_and_next_key?.next_key}</td>

                        <td>
                          <div className="w-full max-w-[150px] bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                            <div
                              className="h-2.5 rounded-full bg-[#d1dbcf]"
                              style={{ width: `${el?.contract_progress?.percentage || 0}%` }}
                            />
                          </div>
                          <p className="text-xs mt-1 text-center">
                            ({el?.contract_progress?.percentage || 0}% complete)
                          </p>
                        </td>
                      </tr>

                      {expandedRows[el.id] && (
                        <tr>
                          <td colSpan={5}>
                            <ExpandedRowContent data={el} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <ContactPagination
            paginationData={paginationData}
            activeTab={activeTab}
            handleNext={handleNext}
            handlePrev={handlePrev}
            onSuccess={onSuccess}
            itemPerPage={itemPerPage}
            handleItemPerPageSelect={(value) => {
              setItemPerPage(value)
              setCurrentPage(1)
            }}
          />
        </div>
      )}

      {(activeTab === 1) && (
        <div className="light-bg-L pb-3 table-container">
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
            {loading ? (
              <Loader />
            ) : listData?.length === 0 ? (
              <p className="body-N text-center mt-6 p-10">No Data Available</p>
            ) : (
              <table className="contact-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    {/* Property */}
                    <th className="green-H">
                      <div className="table-header">
                        Property
                        <img
                          role="button"
                          src={
                            sortBy !== "property_name"
                              ? swap
                              : sortDirection === "desc"
                                ? sortDesc
                                : sortAsce
                          }
                          onClick={() => {
                            setSortBy("property_name");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    {/* DISPOSITION */}
                    <th>
                      <div className="table-header">
                        DISPOSITION
                        <img
                          role="button"
                          src={
                            sortBy !== "disposition"
                              ? swap
                              : sortDirection === "desc"
                                ? sortDesc
                                : sortAsce
                          }
                          onClick={() => {
                            setSortBy("disposition");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    {/* ACQUISITION */}
                    <th>
                      <div className="table-header">
                        ACQUISITION
                        <img
                          role="button"
                          src={
                            sortBy !== "acquisition"
                              ? swap
                              : sortDirection === "desc"
                                ? sortDesc
                                : sortAsce
                          }
                          onClick={() => {
                            setSortBy("acquisition");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>


                    {/* EXECUTED LOI */}
                    <th>
                      <div className="table-header">
                        EXECUTED LOI DATE
                        <img
                          role="button"
                          src={
                            sortBy !== "executed_loi_date"
                              ? swap
                              : sortDirection === "desc"
                                ? sortDesc
                                : sortAsce
                          }
                          onClick={() => {
                            setSortBy("executed_loi_date");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    {/* ORDERED MILESTONES START HERE */}
                    <th>
                      <div className="table-header">
                        PSA DISTRIBUTED
                        <img
                          role="button"
                          src={
                            sortBy !== "psa_distributed"
                              ? swap
                              : sortDirection === "desc"
                                ? sortDesc
                                : sortAsce
                          }
                          onClick={() => {
                            setSortBy("psa_distributed");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        PSA EXECUTED
                        <img
                          role="button"
                          src={
                            sortBy !== "psa_executed"
                              ? swap
                              : sortDirection === "desc"
                                ? sortDesc
                                : sortAsce
                          }
                          onClick={() => {
                            setSortBy("psa_executed");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        EARNEST DEPOSIT DUE
                        <img
                          role="button"
                          src={
                            sortBy !== "earnest_deposit_due"
                              ? swap
                              : sortDirection === "desc"
                                ? sortDesc
                                : sortAsce
                          }
                          onClick={() => {
                            setSortBy("earnest_deposit_due");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        DUE DILIGENCE DELIVERY
                        <img
                          role="button"
                          src={
                            sortBy !== "due_diligence_delivery"
                              ? swap
                              : sortDirection === "desc"
                                ? sortDesc
                                : sortAsce
                          }
                          onClick={() => {
                            setSortBy("due_diligence_delivery");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        Title
                        <img
                          role="button"
                          src={
                            sortBy !== "title"
                              ? swap
                              : sortDirection === "desc"
                                ? sortDesc
                                : sortAsce
                          }
                          onClick={() => {
                            setSortBy("title");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        Cordinate Inspection
                        <img
                          role="button"
                          src={
                            sortBy !== "cordinate_inspection"
                              ? swap
                              : sortDirection === "desc"
                                ? sortDesc
                                : sortAsce
                          }
                          onClick={() => {
                            setSortBy("cordinate_inspection");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        Due Diligence Expiration
                        <img
                          role="button"
                          src={
                            sortBy !== "due_diligence_expiration_date"
                              ? swap
                              : sortDirection === "desc"
                                ? sortDesc
                                : sortAsce
                          }
                          onClick={() => {
                            setSortBy("due_diligence_expiration_date");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        Estoppel & SNDA Ordered
                        <img
                          role="button"
                          src={
                            sortBy !== "estoppel_snda_ordered"
                              ? swap
                              : sortDirection === "desc"
                                ? sortDesc
                                : sortAsce
                          }
                          onClick={() => {
                            setSortBy("estoppel_snda_ordered");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        Financing Approval
                        <img
                          role="button"
                          src={
                            sortBy !== "financing_approval"
                              ? swap
                              : sortDirection === "desc"
                                ? sortDesc
                                : sortAsce
                          }
                          onClick={() => {
                            setSortBy("financing_approval");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        Estoppel & SNDA Received
                        <img
                          role="button"
                          src={
                            sortBy !== "estoppel_snda_received"
                              ? swap
                              : sortDirection === "desc"
                                ? sortDesc
                                : sortAsce
                          }
                          onClick={() => {
                            setSortBy("estoppel_snda_received");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        Commission Invoice & Wiring Instructions Sent
                        <img
                          role="button"
                          src={
                            sortBy !== "comission_invoice_wiring_instruction"
                              ? swap
                              : sortDirection === "desc"
                                ? sortDesc
                                : sortAsce
                          }
                          onClick={() => {
                            setSortBy("comission_invoice_wiring_instruction");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        Closing Scheduled
                        <img
                          role="button"
                          src={
                            sortBy !== "scheduled_closing"
                              ? swap
                              : sortDirection === "desc"
                                ? sortDesc
                                : sortAsce
                          }
                          onClick={() => {
                            setSortBy("scheduled_closing");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">More</div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {listData.map((el, idx) => (
                    <tr
                      key={idx}
                      className="dark-M body-N cursor-pointer hover:bg-gray-50"
                    >
                      {/* PROPERTY */}
                      <td className="head-5 capitalize">
                        <Link to={
                          el?.portfolio === 0
                            ? `/property/${el?.property_id}`
                            : el?.link?.owner_info_type === "contact"
                              ? `/contact/${el?.link?.owner?.id}`
                              : `/company/${el?.link?.owner?.id}`
                        }
                          onClick={(e) => {
                            e.preventDefault();

                            if (el?.portfolio === 0) {
                              navigate(`/property/${el?.property_id}`);
                            } else if (el?.link?.owner_info_type === "contact") {
                              navigate(`/contact/${el?.link?.owner?.id}`);
                            } else if (el?.link?.owner_info_type === "company") {
                              navigate(`/company/${el?.link?.owner?.id}`);
                            }
                          }}>
                          {el?.link?.property_name} <br />
                          {el?.link?.street_address} <br />
                          {el?.link?.state && el?.link?.city ? ` ${el?.link?.city} ,  ${el?.link?.state} ` : ""}
                        </Link>
                      </td>
                      {/* DISPOSITION */}
                      <td>{el?.executed?.offer?.disposition_name}</td>

                      {/* ACQUISITION */}
                      <td>{el?.executed?.offer?.acquistion_name}</td>

                      {/* LOI */}
                      <td>{el?.executed_loi_date}</td>

                      {/* ORDERED MILESTONES */}
                      <td>{el?.purchase_agreement_draft_distributed}</td>
                      <td>{el?.executed_purchase_agreeement}</td>
                      <td className={getStatusColor(el?.earnest_deposite_confirmed_status)}>
                        {el?.earnest_deposite_confirmed}</td>
                      <td className={getStatusColor(el?.due_diligence_delivery_receipt_confirmed_status)}>
                        {el?.due_diligence_delivery_receipt_confirmed}
                      </td>

                      <td className={getStatusColor(el?.title_status)}>
                        {el?.title}
                      </td>

                      <td className={getStatusColor(el?.cordinate_inspection_status)}>
                        {el?.cordinate_inspection}
                      </td>

                      <td className={getStatusColor(el?.due_diligence_expiration_date_status)}>
                        {el?.due_diligence_expiration_date}
                      </td>

                      <td className={getStatusColor(el?.estoppel_snda_ordered_status)}>
                        {el?.estoppel_snda_ordered}
                      </td>

                      <td className={getStatusColor(el?.financing_approval_status)}>
                        {el?.financing_approval}
                      </td>

                      <td className={getStatusColor(el?.estoppel_snda_received_status)}>
                        {el?.estoppel_snda_received}
                      </td>

                      <td className={getStatusColor(el?.comission_invoice_wiring_instruction_status)}>
                        {el?.comission_invoice_wiring_instruction}
                      </td>

                      <td className={getStatusColor(el?.scheduled_closing_status)}>
                        {el?.scheduled_closing}
                      </td>




                      {/* ACTIONS */}
                      <td
                        className="flex gap-3 items-center w-full justify-center actions-menu"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ActionsMenu
                          handleEdit={() => {
                            navigate(`/edit-contact-close/${el?.id}`);
                            setSelectedId(el?.id);
                          }}
                          handleDelete={() => {
                            setSelectedId(el?.id);
                            setShowDeleteModal(true);
                          }}
                          showOtherOption={true}
                          otherOptionTitle={"Close Contract"}
                          handleOtherOption={() => {
                            setShowConfirmModal(true);
                            setSelectedId(el?.id);
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

            )}
          </div>
          <ContactPagination
            paginationData={paginationData}
            activeTab={activeTab}
            handleNext={handleNext}
            handlePrev={handlePrev}
            onSuccess={onSuccess}
            itemPerPage={itemPerPage}
            handleItemPerPageSelect={(value) => {
              setItemPerPage(value)
              setCurrentPage(1)
            }}
          />
        </div>
      )}

      <ConfirmationModal
        showModal={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        handleConfirm={handleConfirm}
      />
      <DeleteConfirmationModal
        showModal={showDeleteModal}
        disable={disableDelete}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={handleDelete}
      />

      <PropertyPortfolioModal
        isOpen={showPropertyModal}
        onClose={() => {
          setShowPropertyModal(false);
          setSelectedProperty(null);
        }}
        id={selectedPortfolioId}
        property={selectedProperty}
      />
    </BaseLayout>
  )
}

export default ContractToClosed
